import {
  animate,
  state,
  style,
  transition,
  trigger,
} from "@angular/animations";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  Output,
  ViewEncapsulation,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { DragDropModule } from "@angular/cdk/drag-drop";
import { AngularSvgIconModule } from "angular-svg-icon";
import { CardModel } from "../../../../swagger/api.client.generated";
import { CardActionMenuComponent } from "../card-action-menu/card-action-menu.component";
import { CardActionMenu } from "../card-action-menu/card-action-menu.models";
import {
  CardAction,
  CardActionEvent,
  ControlOptions,
  UserAttribute,
} from "../card.models";
import { boostThemeEnum } from "@app/services/page.service";
import { CardLocalService } from "../card.service";
import { CardExpandService } from "../../card-expanded/card-expand.service";
import { isMandatoryActivityCard, isUneditedCustomCard } from "../card-type.utils";

@Component({
  selector: "app-card-shell",
  templateUrl: "./card-shell.component.html",
  styleUrls: ["./card-shell.component.scss"],
  standalone: true,
  imports: [CommonModule, DragDropModule, AngularSvgIconModule, CardActionMenuComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger("flipState", [
      state("active", style({ transform: "rotateY(179deg)" })),
      state("inactive", style({ transform: "rotateY(0)" })),
      transition("active => inactive", animate("500ms ease-out")),
      transition("inactive => active", animate("500ms ease-in")),
    ]),
  ],
})
// Structural shell shared by all typed card components (SafetyCard, ActivityCard).
// Owns: CDK drag-drop, card border/background container, complete/selected overlays,
// action menu, and a single <ng-content> slot for icon + content projection.
// Typed card components control visual inputs (backgroundStyle, borderCssClass, etc.).
export class CardShellComponent implements OnDestroy {
  @Input() cardModel: CardModel | null = null;
  /** CSS class string for the card background, e.g. 'background-angel', 'background-activity' */
  @Input() backgroundStyle: string = "";
  /** CSS class for border colour, e.g. 'sc-feelings', 'ac-anchor', 'user' */
  @Input() borderCssClass: string = "";
  /** Top-level card type class: 'safety' | 'activity' */
  @Input() cardTypeClass: string = "";
  /** Detailed type class, e.g. 'activity', 'activity_recommended' — drives icon theme colour */
  @Input() cardTypeCss: string = "";
  @Input() cardSelected: boolean = false;
  @Input() dragDisabled: boolean = false;
  /** ms before a press-and-hold becomes a drag. The carousel passes 0 (instant drag): it keeps
   *  sideways swipes as native scroll via CarouselTouchScrollGuardDirective (a window-capture
   *  touchmove guard) rather than via a start delay, so a downward pull needs no long-press.
   *  Plan cards pass PLAN_CARD_DRAG_START_DELAY — a hold on touch, 0 on mouse — because there
   *  both axes scroll, leaving the hold as the only signal (CDK's cdkDragStartDelay supports
   *  both shapes). Callers that set neither get this conservative default. */
  @Input() dragStartDelay: number | { touch: number; mouse: number } = 250;
  @Input() controls: ControlOptions = new ControlOptions();
  @Input() editMode: boolean = false;
  @Input() hasBorder: boolean = true;
  @Input() isEditable: boolean = false;
  @Input() flip: "active" | "inactive" = "inactive";

  @Output() cardAction = new EventEmitter<CardActionEvent>();
  @Output() userAttributes = new EventEmitter<UserAttribute>();
  /** Emits true when action menu opens, false when it closes/action taken */
  @Output() actionMenuClicked = new EventEmitter<boolean>();

  @HostListener("contextmenu", ["$event"])
  onRightClick(event: MouseEvent) {
    event.preventDefault();
  }

  constructor(
    private cardLocalService: CardLocalService,
    private cd: ChangeDetectorRef,
    private cardExpandService: CardExpandService,
  ) {}

  handleExpandClick(event: MouseEvent) {
    event.stopPropagation();
    if (!this.cardModel) return;
    this.cardExpandService.open(this.cardModel);
  }

  // Null card means the slot has no data to pass to the drop target — disallow drag.
  isDragDisabled(): boolean {
    return !this.cardModel || this.dragDisabled;
  }

  // Mandatory activity cards (dark riverbed bg) and custom cards (coloured bg) use white icons;
  // all other cards use the dark forest-path icon colour.
  get iconThemeColor(): boostThemeEnum {
    return (this.cardTypeCss === "custom-safety" || this.cardTypeCss.includes("activity"))
      ? boostThemeEnum.parchment
      : boostThemeEnum.evergreen;
  }

  // Whether the action menu's DELETE item is shown.
  // Pre-edit custom cards are fixed templates (Edit + Favourite only) — they can never be deleted,
  // in any context. The mandatory-anchor rule is picker-only (via controls.hideDeleteWhenMandatory),
  // where DELETE means "remove from the card deck" and anchors can't leave the library; everywhere
  // else (plan edit/sanctuary) DELETE means "remove from this plan" and stays available for them.
  get canDelete(): boolean {
    if (isUneditedCustomCard(this.cardModel)) return false;
    if (this.controls?.hideDeleteWhenMandatory && isMandatoryActivityCard(this.cardModel)) {
      return false;
    }
    return true;
  }

  // Pre-edit custom cards are templates: EDIT (which fills in the template via the CreateCustomCard
  // path) + FAVOURITE only. Duplicating an empty template is meaningless, so hide DUPLICATE for them
  // everywhere.
  get canDuplicate(): boolean {
    return !isUneditedCustomCard(this.cardModel);
  }

  // Whether the action menu's REMOVE item is shown. Set by plan-edit and Sanctuary, where the
  // destructive action takes the card out of the plan (it stays in the user's deck) rather than
  // deleting it from the deck. The shell suppresses DELETE in these contexts (see template).
  get canRemove(): boolean {
    return !!this.controls?.removeFromPlan;
  }

  // A not-yet-created custom card: its editable menu item is shown as CREATE (not EDIT), and the
  // create flow runs even outside plan edit (picker/library), unlike EDIT which is plan-edit only.
  get isUneditedCustom(): boolean {
    return isUneditedCustomCard(this.cardModel);
  }

  dragEventStarted(e: any) {
    this.cardLocalService.dragStarted(e);
    this.suppressBrowserTouchScroll();
  }
  dragEventEnded(e: any) {
    this.cardLocalService.dragEnded(e);
    this.releaseBrowserTouchScroll();
  }
  dragEventDropped(e: any) { this.cardLocalService.dragDropped(e); }
  dragEventEntered(e: any) { this.cardLocalService.dragEntered(e); }
  dragEventExited(e: any) { this.cardLocalService.dragExited(e); }

  ngOnDestroy(): void {
    // Safety net if the component is torn down mid-drag and cdkDragEnded
    // never fires — otherwise the global listener would leak.
    this.releaseBrowserTouchScroll();
  }

  // While a card is actively being dragged, cancel the browser's own
  // scroll/pan handling for every touchmove. `touch-action` CSS can't do
  // this — its value is locked in at touchstart, so toggling it once CDK
  // confirms a drag has no effect on the in-progress gesture. The browser
  // is already mid-scroll (page panning opposite to the drag, or the
  // carousel viewport scrolling because the touch originated inside it),
  // and preventDefault on touchmove is what actually halts it. Listener
  // is attached only after cdkDragStartDelay (dragStartDelay input) has elapsed
  // and CDK has actually started a drag, so a short swipe over the carousel still
  // scrolls normally. On the carousel (delay 0) a sideways swipe never starts a
  // drag because CarouselTouchScrollGuardDirective withholds horizontal-gesture
  // touchmoves from CDK (window-capture stopPropagation), so cdkDragStarted never
  // fires for a scroll and this listener stays off for it.
  private touchMoveBlocker = (event: TouchEvent) => {
    if (event.cancelable) event.preventDefault();
  };

  private touchMoveBlockerActive = false;

  private suppressBrowserTouchScroll(): void {
    if (this.touchMoveBlockerActive) return;
    document.addEventListener("touchmove", this.touchMoveBlocker, { passive: false });
    this.touchMoveBlockerActive = true;
  }

  private releaseBrowserTouchScroll(): void {
    if (!this.touchMoveBlockerActive) return;
    document.removeEventListener("touchmove", this.touchMoveBlocker);
    this.touchMoveBlockerActive = false;
  }

  handleCardAction(action: CardAction) {
    this.cardAction.emit({ action, card: this.cardModel });
  }

  handleActionMenuClick(event: CardActionMenu) {
    this.actionMenuClicked.emit(event === "open");
    if (event === "open") return;

    switch (event) {
      case "swap":
        this.handleCardAction("swap");
        break;
      case "delete":
        this.handleCardAction("delete");
        this.reportDelete();
        break;
      case "remove":
        // Removal only takes the card out of the current plan — it stays in the deck,
        // so (unlike delete) we do not reportDelete() the loss of deck membership.
        this.handleCardAction("remove");
        break;
      case "favorite":
        this.handleFavorite();
        break;
      case "create":
        this.handleCardAction("create");
        break;
      case "edit":
        this.handleCardAction("edit");
        break;
      case "duplicate":
        this.handleCardAction("duplicate");
        break;
    }
  }

  private handleFavorite() {
    if (!this.cardModel) return;
    // Optimistic UI; service mutates the card, persists, and reverts on failure.
    this.cardLocalService
      .toggleFavorite(this.cardModel)
      .subscribe(() => this.cd.markForCheck());
    this.cd.markForCheck();
  }

  // Delete emits via userAttributes (not cardAction) so the parent can record the loss
  // of deck membership alongside the deletion event for user-attribute tracking.
  private reportDelete() {
    if (!this.cardModel) return;
    this.userAttributes.emit({
      isFavourite: this.cardModel.isPatientFavourite,
      isUserDeck: this.cardModel.isPatientDeck,
      cardId: this.cardModel.cardId,
      delete: true,
    });
  }
}
